import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Stack,
  Divider,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { FaTrashAlt } from "react-icons/fa";

import { ref, get } from "firebase/database";
import { rtdb } from "../services/firebase";
import { useAuth } from "../context/AuthContext";

export default function CartPage() {

  const navigate = useNavigate();
  const { user, cart, removeFromCart } = useAuth();

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [discountLimit, setDiscountLimit] = useState(0);

  const [isWholesale, setIsWholesale] = useState(false);
  const [subtotalNormal, setSubtotalNormal] = useState(0);

  const [toastMessage, setToastMessage] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);

  /**
   * Monta o carrinho visual a partir do cart (context)
   */
  useEffect(() => {

    async function buildCart() {

      if (!cart.length) {
        setCartItems([]);
        setLoading(false);
        return;
      }

      try {

        const discountSnap = await get(ref(rtdb, "app/config/discount"));
        const discountValue = discountSnap.exists()
          ? Number(discountSnap.val())
          : 0;

        setDiscountLimit(discountValue);

        const items = await Promise.all(
          cart.map(async (item) => {
            const snap = await get(ref(rtdb, `products/${item.productId}`));
            if (!snap.exists()) return null;

            const product = snap.val();

            const rawPrice = product.precos.isPromo
              ? product.precos.promo
              : product.precos.normal;

            const normalPrice = parseCurrency(rawPrice);
            const wholesalePrice = parseCurrency(product.precos?.discount);

            return {
              productId: item.productId,
              title: product.nome,
              description: product.descricao,
              image: product.imagens?.[0],
              flavor: item.flavor,
              normalPrice,
              wholesalePrice,
              quantity: item.quantity,
            };
          })
        );

        const validItems = items.filter(Boolean);

        // subtotal SEM desconto (varejo)
        const normalSubtotal = validItems.reduce(
          (acc, item) => acc + item.normalPrice * item.quantity,
          0
        );

        setSubtotalNormal(normalSubtotal);

        // decide se vira atacado
        setIsWholesale(
          discountValue > 0 && normalSubtotal >= discountValue
        );

        setCartItems(validItems);

      } catch (err) {
        console.error("Erro ao montar carrinho:", err);
      } finally {
        setLoading(false);
      }
    }

    buildCart();
  }, [cart]);

  function parseCurrency(value) {
    if (!value) return 0;

    return Number(
      value
        .replace("R$", "")
        .replace(/\s/g, "")
        .replace(".", "")
        .replace(",", ".")
    );
  }

  const goCheckout = () => {

    if (!user) {
      setToastMessage("Faça login para prosseguir!");
      setToastOpen(true);
      return;
    }

    navigate("/checkout");

  };

  const subtotal = cartItems.reduce((acc, item) => {
    const price = isWholesale
      ? item.wholesalePrice
      : item.normalPrice;

    return acc + price * item.quantity;
  }, 0);

  if (loading) {
    return (
      <Box py={6} textAlign="center">
        <Typography>Carregando carrinho...</Typography>
      </Box>
    );
  }

  if (!cartItems.length) {
    return (
      <Box py={20} textAlign="center">
        <Typography variant="h5" mb={2}>
          Seu carrinho está vazio
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/produtos")}
        >
          Ver produtos
        </Button>
      </Box>
    );
  }

  return (
    <Box maxWidth="1100px" mx="auto" px={2} py={4}>
      <Typography variant="h4" fontWeight={600} mb={4}>
        Seu carrinho
      </Typography>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "2.2fr 1fr" }}
        gap={4}
        alignItems="flex-start"
      >
        {/* LISTA */}
        <Box width="100%">
          <Stack spacing={2}>
            {cartItems.map((item) => (
              <Card
                key={`${item.productId}-${item.flavor}`}
                onClick={() => navigate(`/produto/${item.productId}`)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: { xs: 1.5, md: 2 },
                  cursor: "pointer",
                  borderRadius: 3,
                  position: "relative",
                  transition: "0.25s",
                  "&:hover": {
                    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardMedia
                  component="img"
                  image={item.image}
                  alt={item.title}
                  sx={{
                    width: { xs: 70, md: 90 },
                    height: { xs: 70, md: 90 },
                    objectFit: "contain",
                  }}
                />

                <CardContent sx={{ p: 0, flex: 1 }}>
                  <Typography fontWeight={600}>
                    {item.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mt={0.5}
                  >
                    {item.description}
                  </Typography>

                  <Typography variant="body2" mt={0.5}>
                    Sabor: <strong>{item.flavor}</strong>
                  </Typography>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={1.5}
                  >
                    <Typography variant="body2">
                      Qtd: {item.quantity}
                    </Typography>

                    <Typography fontWeight={600}>
                      {(() => {
                        const price = isWholesale
                          ? item.wholesalePrice
                          : item.normalPrice;

                        return `R$ ${(price * item.quantity).toFixed(2)}`;
                      })()}
                    </Typography>

                    {isWholesale ? (
                      <Typography
                        variant="body2"
                        sx={{ color: "#4caf50", fontWeight: 600 }}
                      >
                        🎉 Preço de atacado aplicado
                      </Typography>
                    ) : null}
                  </Box>
                </CardContent>

                {/* 🗑️ BOTÃO REMOVER */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFromCart(item.productId, item.flavor);
                    setToastMessage("Item removido com sucesso!");
                    setToastOpen(true);
                  }}
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    color: "#999",
                    "&:hover": {
                      color: "#d32f2f",
                      backgroundColor: "rgba(211,47,47,0.08)",
                    },
                  }}
                >
                  <FaTrashAlt size={16} />
                </IconButton>
              </Card>
            ))}
          </Stack>
        </Box>

        {/* RESUMO */}
        <Box
          width="100%"
          maxWidth={360}
          position={{ xs: "static", md: "sticky" }}
          top={120}
        >
          <Card
            sx={{
              background: "linear-gradient(135deg, #1E2B4E, #051129)",
              color: "#fff",
              borderRadius: 4,
              p: 3,
            }}
          >
            <Typography variant="h6" mb={2}>
              Resumo
            </Typography>

            <Stack spacing={3}>
              <Row label="Subtotal" value={`R$ ${subtotal.toFixed(2)}`} />
              <Row label="Frete" value="Calculado no checkout" />
            </Stack>

            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.15)" }} />

            <Row
              label="Total"
              value={`R$ ${subtotal.toFixed(2)}`}
              bold
            />

            <Button
              fullWidth
              sx={{
                mt: 3,
                py: 1.4,
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, #ff7a00, #ff9a00)",
                color: "#fff",
                fontWeight: 600,
              }}
              onClick={goCheckout}
            >
              Ir para o checkout
            </Button>
          </Card>
        </Box>
      </Box>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ mb: { xs: 4, md: 2 } }}
      >
        <Alert severity="success">{toastMessage}</Alert>
      </Snackbar>
    </Box>
  );
}

function Row({ label, value, bold }) {
  return (
    <Box display="flex" justifyContent="space-between" gap={2}>
      <Typography fontWeight={bold ? 600 : 400}>
        {label}
      </Typography>
      <Typography fontWeight={bold ? 600 : 400}>
        {value}
      </Typography>
    </Box>
  );
}
